import React, { FunctionComponent, memo, useEffect } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import TextInputFieldC from "../../../../Components/TextInput/TextInputFieldC";
import Select from "../../../../Components/Select/AutoComplete";
import { AutocompleteOption } from "../../../../Components/Select/ISelectProps";
import { useAppDispatch } from "../../../../Redux/Store/Hooks";
import { Session_Crud } from "../../../../Redux/Slices/CRM/Leads/Session/Session_crud";
import { ISession } from "./ISession";



interface SessionFormProps {
  formRef: React.RefObject<HTMLFormElement>;
  initialValues: Partial<ISession>;
  setInitialValues: React.Dispatch<React.SetStateAction<Partial<ISession>>>;
}

const sessionSchema = z.object({
  session_id: z.string().default("0"),
  description: z.string().min(1, "Description required"),
  tutor_id: z.number().min(1, "Tutor ID required"),
  session_date: z.string().min(1, "Date required"),
  mode: z.string().min(1, "Mode required"),
  duration_minutes: z.number().min(1, "Duration required"),
  meeting_link: z.string().optional(),
  meet_code: z.string().optional(),
  venue: z.string().optional(),
  status: z.boolean().default(true),
  is_delete: z.boolean().default(false),
});

const SessionForm: FunctionComponent<SessionFormProps> = ({
  formRef,
  initialValues,
  setInitialValues,
}) => {
  const dispatch = useAppDispatch();

  const methods = useForm<ISession>({
    resolver: zodResolver(sessionSchema),
    defaultValues: {
      session_id: "0",
      description: "",
      tutor_id: 0,
      session_date: "",
      mode: "online",
      duration_minutes: 0,
      meeting_link: "",
      meet_code: "",
      venue: "",
      status: true,
      is_delete: false,
      ...initialValues,
    },
  });

  useEffect(() => {
    if (initialValues && Object.keys(initialValues).length > 0)
      methods.reset(initialValues);
  }, [initialValues, methods]);

  useEffect(() => {
    return () => setInitialValues({});
  }, [setInitialValues]);

  const statusOptions: AutocompleteOption[] = [
    { label: "Active", value: true },
    { label: "Inactive", value: false },
  ];

  const modeOptions: AutocompleteOption[] = [
    { label: "Online", value: "online" },
    { label: "Offline", value: "offline" },
  ];

  const onSubmit: SubmitHandler<ISession> = (data) => {
    const payload: ISession = {
      ...data,
      tutor_id: Number(data.tutor_id),
    };
    dispatch(Session_Crud(payload));
  };

  return (
    <FormProvider {...methods}>
      <form ref={formRef} onSubmit={methods.handleSubmit(onSubmit)} className="form_double_input">
        <TextInputFieldC name="description" label="Description" type="text" />
        <TextInputFieldC name="tutor_id" label="Tutor ID" type="number" />
        <TextInputFieldC name="session_date" label="Session Date" type="text" />
        <Select
          name="mode"
          label="Mode"
          options={modeOptions}
          value={modeOptions.find(o => o.value === methods.getValues("mode")) || null}
          onChange={(_, val) => methods.setValue("mode", typeof val === "object" && val !== null ? val.value : val || "online")}
        />
        <TextInputFieldC name="duration_minutes" label="Duration (mins)" type="number" />
        <TextInputFieldC name="meeting_link" label="Meeting Link" type="text" />
        <TextInputFieldC name="meet_code" label="Meeting Code" type="text" />
        <TextInputFieldC name="venue" label="Venue" type="text" />
        <Select
          name="status"
          label="Status"
          options={statusOptions}
          value={statusOptions.find(o => o.value === methods.getValues("status")) || null}
          onChange={(_, val) => methods.setValue("status", typeof val === "object" && val !== null ? val.value : val || true)}
        />
      </form>
    </FormProvider>
  );
};

export default memo(SessionForm);
